export const state = {
    changeList: [], // change list
    config: {}, // configuration data
    list: [], // config list
    tabs: [], // open config tabs
    activeTab: 'clientManagement',
    template: [], // configuration template
    templateList: [],
    updatedValues: {},
    bulkUpdateIds: [],
    bulkUpdateMode: undefined,
    discardClient: {},
    promoteClients: [],
    existingIds: [],
    validationRequests: [],
};

import mergeWith from "lodash/mergeWith";
import ClientService from '@/services/ClientService';
import router from '@/router'
import specialRuleIds from "@/util/specialRuleIds";
import visibleFields from "@/util/visibleFields";
import existsInTemplate from "@/util/existsInTemplate";
import fieldMap from "@/components/config-field/field-map.json";

import transform from "lodash/transform";
import isEqual from "lodash/isEqual";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";

const configMerge = (...values) => {
    const mergeCustomizer = (objValue, srcValue) => {
        // Arrays that have been updated inside of configs need to fully overwrite the stored values, not just merge them
        if (Array.isArray(objValue)) {
            return srcValue;
        }
    };

    return mergeWith({}, ...values, mergeCustomizer);
}


const getFieldMapValue = ({ category, fieldId, value }) => (
    fieldMap[category] &&
    fieldMap[category][fieldId] &&
    fieldMap[category][fieldId][value]
);

/*
    Actions hook up to the interface, but they do not touch the store data directly.
    They can be rather complicated and will generally lean on one or more mutations.
    Actions are also the communication layer with external sources (API).

    Actions come with access to four contexts ({ state, getters, commit, dispatch })
        state = direct data access... avoid use
        getters = indirect data access... this is good
        commit = call a mutation
        dispatch = for us, dispatch will almost certainly be an axios api call
*/
export const actions = {
    /**
     * List clients
     * @param {*} contexts - commit 
     */
    async list({ commit, dispatch, getters } = {}) {
        await ClientService.list({ TenantId: getters.getTenantId }, dispatch).then((response) => {
            commit('SET_LIST', response.data.ClientInfoList);
        }).catch(() => {
            commit('SET_LIST', []);
        });
    },

    /**
     * List client templates
     * @param {*} contexts - commit 
     */
    async listTemplates({ commit, dispatch, getters } = {}) {
        await ClientService.list({ TenantId: getters.getTenantId, TemplatesOnly: true }, dispatch).then((response) => {
            commit('SET_TEMPLATE_LIST', response.data.ClientInfoList);
        }).catch(() => {
            commit('SET_TEMPLATE_LIST', []);
        });
    },

    /**
     * Fetch existing IDs
     * @param {*} contexts - commit 
     */
    async fetchExistingIds({ commit, dispatch, getters } = {}) {
        const configs = await ClientService.list({ TenantId: getters.getTenantId }, dispatch);
        const templates = await ClientService.list({ TenantId: getters.getTenantId, TemplatesOnly: true }, dispatch);

        commit('SET_EXISTING_IDS', [...configs.data.ClientInfoList.map(config => config.ClientId), ...templates.data.ClientInfoList.map(template => template.ClientId)]);
    },

    /**
     * List client changes
     * @param {*} contexts - commit 
     */
    async changeList({ commit, dispatch, getters } = {}, { ClientId } = {}) {
        await ClientService.changeList({ ClientId, TenantId: getters.getTenantId }, dispatch).then((response) => {
            commit('SET_CHANGE_LIST', response.data.ClientInfoChangeList);
        }).catch(() => {
            commit('SET_CHANGE_LIST', []);
        });
    },

    /**
     * Edit client change
     * @param {*} contexts - commit 
     */
    async editChange({ dispatch, getters } = {}, { ClientId, ChangeTitle, ChangeDetails, Version } = {}) {
        await ClientService.editChange({ ClientId, TenantId: getters.getTenantId, ChangeTitle, ChangeDetails, Version }, dispatch).then(() => {
            dispatch("changeList", { ClientId });
        }).catch(() => {
        });
    },

    /**
     * Restore client change
     * @param {*} contexts - commit 
     */
    async restoreChange({ dispatch, getters } = {}, { ClientId, ClientInfoRuleId, ChangeTitle, ChangeDetails, Version } = {}) {
        await ClientService.restoreChange({ ClientId, ClientInfoRuleId, TenantId: getters.getTenantId, ChangeTitle, ChangeDetails, Version }, dispatch).then(() => {
            dispatch("changeList", { ClientId });
            dispatch("retrieveConfig", { ClientId, ClientInfoRuleId });
            dispatch("list");
            router.push('/clients');
        }).catch(() => {
        });
    },

    /**
     * Create/update client configuration
     * @param {*} contexts - commit 
     */
    async saveConfig({ commit, dispatch, getters }, { ClientId, ClientIdList, ClientInfoRuleId, ChangeTitle, ChangeDetails, IsTemplate = false, ArrayMergeOption } = {}) {
        let configToSave = getters.getUpdatedConfigValues[ClientInfoRuleId];
        configToSave = expandConfigData(configToSave);
        const TemplateName = IsTemplate && (configToSave.Info && configToSave.Info.TemplateName) || (getters.getConfig[ClientInfoRuleId] && getters.getConfig[ClientInfoRuleId]['Info'] && getters.getConfig[ClientInfoRuleId]['Info'].TemplateName);
        const ConfigLabel = configToSave.Info && configToSave.Info.ConfigLabel;
        const CopyFromClientId = configToSave.Info && configToSave.Info.CopyFromClientId;

        if (ClientInfoRuleId === 'bulkEdit' || ClientInfoRuleId === 'bulkDelete') ClientInfoRuleId = undefined;

        delete configToSave.Info;
        if (ClientInfoRuleId && ConfigLabel) {
            let updateInfoRecord = {};
            updateInfoRecord.ConfigLabel = ConfigLabel;
            configToSave.Info = updateInfoRecord;
        }

        if (ClientInfoRuleId === 'createTemplate') ClientInfoRuleId = 'New';

        if (configToSave.ClientInformation && configToSave.ClientInformation.ClientId) delete configToSave.ClientInformation.ClientId;

        // TODO: Refactor this so that ClientId is always being passed into saveConfig as an array with the proper object structure in the first place
        const ClientIdArray = ClientIdList ? ClientIdList.map(ClientId => ({ ClientId })) : [{ ClientId, ClientInfoRuleId, IsTemplate, TemplateName, CopyFromClientId }]

        let wasSaveToDBSuccessful = false;
        await ClientService.save({ ClientIdList: ClientIdArray, TenantId: getters.getTenantId, ClientInfoRule: configToSave, ChangeTitle, ChangeDetails, ArrayMergeOption }, dispatch).then((response) => {
            if (response && response.data && response.data.messages) {
                wasSaveToDBSuccessful = true;
                // The structure is an array but there should only ever be one message in the list
                for (const message of response.data.messages) {
                    const returnedClientInfoRuleId = message.clientInfoId;

                    const currentConfig = getters.getConfig[returnedClientInfoRuleId] || {};
                    if (configToSave.Info) {
                        configToSave.Info.IsPromoted = false;
                        configToSave.Info.ClientInfoRuleId = returnedClientInfoRuleId;
                    }
                    let mergedConfig = configMerge(currentConfig, configToSave);
                    // Make sure to store ClientId with the config for newly created configs
                    if (ClientInfoRuleId === 'New') {
                        mergedConfig = configMerge(mergedConfig, { Info: { ClientId } });
                        dispatch("clearNewConfigValues");
                    }
                    commit('SET_CONFIG', { ClientInfoRuleId: returnedClientInfoRuleId, data: mergedConfig });

                    dispatch(IsTemplate ? "listTemplates" : "list"); // refresh list after saving config

                    commit('SET_IS_PROMOTED', { clientInfoRuleId: returnedClientInfoRuleId, isPromoted: false });

                    if (IsTemplate && ClientInfoRuleId === 'New') {
                        router.push('/clients/templates?action=savedTemplate');
                    }
                    if (!IsTemplate && !ClientIdList) {
                        dispatch("openTab", { ClientId, ClientInfoRuleId: returnedClientInfoRuleId, retrieveConfig: false });
                        const tabName = configToSave.ClientInformation && configToSave.ClientInformation.LegalName || ClientId;
                        if (tabName) dispatch('setTabName', { ClientInfoRuleId: returnedClientInfoRuleId, name: tabName });
                        if (getters.getChangeHistoryOpen) dispatch('changeList', { ClientId });
                    }

                    dispatch("notifications/addNotification", {
                        status: 'success',
                        title: IsTemplate ? 'Template Saved' : 'Change Committed',
                        message: IsTemplate ? 'If you need to make any changes you can always edit this template' : 'If you need to go back to a previous commit you can access this in the change history',
                    });
                }
            } else {
                dispatch("notifications/addNotification", {
                    status: 'error',
                    message: 'There was an error in committing the changes',
                });
            }
        }).catch((error) => {
            commit('SET_CONFIG', { ClientInfoRuleId, data: {} });
            throw (error);
        });

        if (wasSaveToDBSuccessful) {
            await ClientService.saveToQFD({ ClientIdList: ClientIdArray, TenantId: getters.getTenantId, ClientInfoRule: configToSave, ClientInfoRuleId, ChangeTitle, ChangeDetails }, dispatch);
        }
    },

    /**
     * Get the configuration data from qfd
     * @param {*} contexts - commit 
     */
    async retrieveConfigFromQFD({ commit, dispatch, getters }, { ClientId, ClientInfoRuleId }) {
        let currentConfig = getters.getConfig[ClientInfoRuleId];
        await ClientService.retrieveFromQFD({ ClientId }, dispatch).then((response) => {
            let qfdConfig = response && response.data && response.data.ClientInfoRule;
            if (qfdConfig) {
                // First filter the config so it only has what our template supports
                qfdConfig = transformConfigFromTemplate(qfdConfig, getters.getConfigTemplate);
                
                /* https://davidwells.io/snippets/get-difference-between-two-objects-javascript */
                const difference = (origObj, newObj) => {
                    function changes(newObj, origObj) {
                        let arrayIndexCounter = 0
                        return transform(newObj, function (result, value, key) {
                            if (!isEqual(value, origObj[key])) {
                                let resultKey = isArray(origObj) ? arrayIndexCounter++ : key
                                result[resultKey] = (isObject(value) && isObject(origObj[key])) ? changes(value, origObj[key]) : value
                            }
                        })
                    }
                    return changes(newObj, origObj)
                }
                const diff = difference(currentConfig, qfdConfig);
                
                if (diff) {
                    const getDifferenceMessage = (objectProperty, message) => {
                        for (const innerProp in objectProperty) {
                            if (objectProperty[innerProp] instanceof Object) {
                                message = getDifferenceMessage(objectProperty[innerProp], message);
                            } else {
                                message = innerProp + "=" + objectProperty[innerProp] + "\n";
                            }
                        }
                        return message;
                    }
                    let hasUpdate = false;
                    let differenceMessage = "";
                    if (diff.Info) {
                        delete diff.Info;
                    }
                    for (const category in diff) {
                        if (Object.keys(diff[category]).length > 0) {
                            hasUpdate = true;
                            differenceMessage += category.replace('AppSpecific', '') + ":\n";
                            differenceMessage += getDifferenceMessage(diff[category], differenceMessage);
                            differenceMessage += "\n";
                        }
                    }
                    if (hasUpdate) {
                        commit('SET_UPDATED_VALUES', { ClientInfoRuleId: ClientInfoRuleId, updatedValues: diff });
                        dispatch("notifications/addNotification", {
                            status: 'success',
                            title: 'Changes',
                            message: differenceMessage,
                        });
                    } else {
                        dispatch("notifications/addNotification", {
                            status: 'success',
                            title: 'Changes',
                            message: "No differences were identified",
                        });
                    }

                }

            }
        });
    },

    /**
     * Get the configuration data
     * @param {*} contexts - commit 
     */
    async retrieveConfig({ commit, dispatch, getters }, { ClientInfoRuleId, ClientId }) {
        await ClientService.retrieve({ TenantId: getters.getTenantId, ClientInfoRuleId, ClientId }, dispatch).then((response) => {
            commit('SET_CONFIG', { ClientInfoRuleId, data: response.data });
            const tabName = response.data.ClientInformation && response.data.ClientInformation.LegalName || ClientId;
            dispatch('setTabName', { ClientInfoRuleId, name: tabName });
        }).catch(() => {
            commit('SET_CONFIG', { ClientInfoRuleId, data: {} });
        });
    },

    /**
     * Get the configuration template
     * @param {*} contexts - commit 
     */
    async retrieveConfigTemplate({ commit, dispatch, getters }, tenantId) {
        if (!tenantId) {
            tenantId = getters.getTenantId;
        }
        await ClientService.retrieveTemplate({ TenantId: tenantId }, dispatch).then((response) => {
            const assets = response.data.TemplateAssets;
            let template = response.data;
            for (let property in template) {
                if (property !== "TemplateAssets") {
                    template[property]["AvailableFieldList"] = replaceAssetReferences(assets, template[property]["AvailableFieldList"]);
                }
            }

            commit('SET_CONFIG_TEMPLATE', template);
        }).catch(() => {
            commit('SET_CONFIG_TEMPLATE', []);
        });
    },

    /**
     * Set config value
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - rule id
     * @param {String} category - category of config item
     * @param {String} fieldId - identifier of config item
     * @param {String} value - value of config item
     */
    setConfigValue({ commit }, { ClientInfoRuleId, category, fieldId, value }) {
        commit('SET_CONFIG_VALUE', { ClientInfoRuleId, category, fieldId, value });
    },

    /**
     * Unset config value
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - rule id
     * @param {String} category - category of config item
     * @param {String} fieldId - identifier of config item
     */
    unsetConfigValue({ commit }, { ClientInfoRuleId, category, fieldId, parentField }) {
        commit('UNSET_CONFIG_VALUE', { ClientInfoRuleId, category, fieldId, parentField });
    },


    /**
     * Clear updated config values for new client
     * @param {*} contexts - commit
     */
    clearNewConfigValues({ commit }) {
        commit('CLEAR_NEW_CONFIG_VALUES');
    },

    /**
     * Open config tab
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - id of rule
     */
    openTab({ commit, dispatch }, { ClientInfoRuleId, ClientId, retrieveConfig = true }) {
        commit('OPEN_TAB', { ClientInfoRuleId, ClientId });
        if (retrieveConfig) dispatch("retrieveConfig", { ClientInfoRuleId, ClientId });
        dispatch("setActiveTab", ClientInfoRuleId);
    },
    /**
     * Close config tab
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - id of rule
     */
    closeTab({ commit, dispatch }, { ClientInfoRuleId }) {
        dispatch('closeRightPanel');
        commit('CLOSE_TAB', { ClientInfoRuleId });
    },
    /**
     * Set active tab
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - info rule id of tab to set active
     */
    setActiveTab({ commit, dispatch }, ClientInfoRuleId) {
        dispatch('closeRightPanel');
        commit('SET_TAB', ClientInfoRuleId);
    },
    /**
     * Set tab name
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - id of rule
     */
    setTabName({ commit }, { ClientInfoRuleId, name }) {
        commit('SET_TAB_NAME', { ClientInfoRuleId, name });
    },
    /**
     * Open the change history panel
     * @param {*} contexts - commit, getters 
     */
    openChangeHistory({ commit, dispatch }, { ClientInfoRuleId }) {
        dispatch('closeRightPanel');
        commit("SET_RIGHT_PANEL_TITLE", "Change History");
        dispatch('rightPanelTransitioning');
        commit("OPEN_CHANGE_HISTORY", { ClientInfoRuleId });
    },
    /**
     * Close the change history panel
     * @param {*} contexts - commit, getters 
     */
    closeChangeHistory({ commit, dispatch }, { ClientInfoRuleId }) {
        dispatch('rightPanelTransitioning');
        commit("CLOSE_CHANGE_HISTORY", { ClientInfoRuleId });
    },
    setSubtabValidity({ commit }, { ClientInfoRuleId, tab, value }) {
        commit("SET_SUBTAB_VALIDITY", { ClientInfoRuleId, tab, value });
    },
    /**
     * Set bulk update ids
     * @param {*} contexts - commit
     * @param {Array} ClientIds - array of ids of clients
     */
    bulkUpdate({ commit }, { ClientIds }) {
        commit('SET_BULK_UPDATE_IDS', { ClientIds });
        router.push('/clients/bulkUpdate');
    },
    /**
     * Remove bulk update id
     * @param {*} contexts - commit
     * @param {String} ClientId - id of client
     */
    removeBulkUpdateId({ commit }, { ClientId }) {
        commit('REMOVE_BULK_UPDATE_ID', { ClientId });
    },
    /**
     * Set bulk update mode
     * @param {*} contexts - commit
     * @param {String} mode - bulk update mode (edit or delete)
     */
    bulkUpdateMode({ commit }, mode) {
        commit('SET_BULK_UPDATE_MODE', mode);
    },

    /**
     * Edit template
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - id of rule
     * @param {String} ClientId - id of client
     */
    editTemplate({ dispatch }, { ClientInfoRuleId, ClientId }) {
        dispatch("retrieveConfig", { ClientInfoRuleId, ClientId });
        router.push(`/clients/templates/edit/${ClientInfoRuleId}`);
    },
    /**
     * Reset bulk settings
     * @param {*} contexts - commit
     */
    resetBulkSettings({ commit }) {
        commit('RESET_BULK_SETTINGS');
    },

    /**
     * Create from template
     * @param {*} contexts - commit
     * @param {String} ClientInfoRuleId - template info rule id
     * @param {String} ClientId - id of client
     */
    async createFromTemplate({ commit, dispatch }, { ClientInfoRuleId, ClientId }) {
        await dispatch("retrieveConfig", { ClientInfoRuleId, ClientId });
        await commit('COPY_CONFIG_TO_NEW', { ClientInfoRuleId });
        dispatch("openCreateClientModal");
    },

    /**
     * Start the discard process
     * @param {*} contexts - commit
     * @param {Object} clients - clientId and clientInfoRuleId
     */
    async startDiscard({ commit }, client ){
        commit('SET_DISCARD_CLIENT', client);
        router.push('/clients/discard/review');
    },

    /**
     * set discard client details
     * @param {*} contexts - commit
     * @param {Object} clients - client discard object containing all details for restore
     */
    async setDiscardClient({ commit }, client ){
        commit('SET_DISCARD_CLIENT', client);
    },

    /**
     * Start the promote process
     * @param {*} contexts - commit
     * @param {Array} clients - array of client ids and info rule ids
     */
    async startPromote({ commit }, { clients }) {
        commit('SET_PROMOTE_CLIENTS', { clients });
        router.push('/clients/promote/review');
    },
    /**
    * Update a promote client
    * @param {*} contexts - commit
    */
    async updatePromoteClient({ commit }, { ClientId, ChangeDetails, ChangeTitle }) {
        commit('UPDATE_PROMOTE_CLIENT', { ClientId, ChangeDetails, ChangeTitle });
    },
    /**
     * Remove promote client
     * @param {*} contexts - commit
     * @param {String} ClientId - id of client
     */
    removePromoteClient({ commit }, { ClientId }) {
        commit('REMOVE_PROMOTE_CLIENT', { ClientId });
    },

    /**
     * Promote config
     * @param {*} contexts - commit
     */
    async promote({ commit, dispatch, getters }, { clients }) {
        var promoteRequest = {
            TenantId: getters.getTenantId,
            ClientIdList: []
        }
        clients.map((client) => {
            promoteRequest.ClientIdList.push({ ChangeDetails: client.ChangeDetails, ChangeTitle: client.ChangeTitle, ClientId: client.clientId, ClientInfoRuleId: client.clientInfoRuleId });
        });
        await ClientService.promote(promoteRequest, dispatch).then(() => {
        }).catch(() => {
            return;
        });
        for (var client of clients) {
            const isPromotedUpdate = {
                clientInfoRuleId: client.clientInfoRuleId,
                isPromoted: true
            };
            commit('SET_IS_PROMOTED', isPromotedUpdate);
        }
        dispatch("notifications/addNotification", {
            status: 'success',
            title: 'Change Promoted',
            message: "If you need to go back to a previous commit you can access this in the Change History",
        });
        router.push('/clients');
    },

    validateConfig({ commit }, configName) {
        commit('VALIDATE_CONFIG', configName);
    },

    clearValidateConfig({ commit }, configName) {
        commit('CLEAR_VALIDATE_CONFIG', configName);
    },

};

/*
    Mutations are methods that act on the state data directly.
    They should be incredibly basic and perform a single task.

    Mutations always take in the current 'state' and a payload.
*/
export const mutations = {
    /**
     * Set configuration list
     * @param {*} state - current state
     * @param {Object} list - configuration list
     */
    SET_LIST(state, list) {
        for (var client of list) {
            client.PromotionStatus = (client.IsPromoted ? "Promoted" : "Not Promoted");
        }
        state.list = list;
    },
    /**
     * Set config template list
     * @param {*} state - current state
     * @param {Object} list - template list
     */
    SET_TEMPLATE_LIST(state, list) {
        state.templateList = list;
    },
    /**
     * Set existing ids list
     * @param {*} state - current state
     * @param {Object} ids - ids list
     */
    SET_EXISTING_IDS(state, ids) {
        state.existingIds = ids;
    },
    /**
     * Set change list
     * @param {*} state - current state
     * @param {Object} list - change list
     */
    SET_CHANGE_LIST(state, list) {
        state.changeList = list;
    },
    /**
     * Set configuration data
     * @param {*} state - current state
     * @param {String} ClientInfoRuleId - rule id
     * @param {Object} data - configuration data
     */
    SET_CONFIG(state, { ClientInfoRuleId, data }) {
        const currentConfig = state.config[ClientInfoRuleId] || {};
        const mergedConfig = Object.assign({}, currentConfig, data);
        state.config = Object.assign({}, state.config, { [ClientInfoRuleId]: mergedConfig });
        // clear any existing updatedValues for config
        state.updatedValues = Object.assign({}, state.updatedValues, { [ClientInfoRuleId]: {} });
        state.template = replaceConfigReferences(ClientInfoRuleId, state.template, state.config);
    },
    /**
     * Set configuration template
     * @param {*} state - current state
     * @param {Object} data - configuration template data
     */
    SET_CONFIG_TEMPLATE(state, data) {
        state.template = data;
    },
    /**
     * Set config value
     * @param {*} state - current state
     * @param {String} ClientInfoRuleId - rule id
     * @param {String} category - category of config item
     * @param {String} fieldId - identifier of config item
     * @param {String} value - value of config item
     */
    SET_CONFIG_VALUE(state, { ClientInfoRuleId, category, fieldId, value }) {
        const currentClientInfoRuleIdData = state.updatedValues[ClientInfoRuleId] || {};
        const currentCategoryData = state.updatedValues[ClientInfoRuleId] && state.updatedValues[ClientInfoRuleId][category] || {};
        let mergedCategoryData = {};
        if (typeof value === "object") {
            let mergedValue = {};
            if (currentCategoryData[fieldId] === undefined || Array.isArray(value)) {
                mergedValue = { [fieldId]: value };
            } else {
                mergedValue = { [fieldId]: Object.assign({}, currentCategoryData[fieldId], value) };
            }
            mergedCategoryData = Object.assign({}, currentCategoryData, mergedValue);
        } else {
            mergedCategoryData = Object.assign({}, currentCategoryData, { [fieldId]: value });
        }
        const mergedClientInfoRuleIdData = Object.assign({}, currentClientInfoRuleIdData, { [category]: mergedCategoryData });
        state.updatedValues = Object.assign({}, state.updatedValues, { [ClientInfoRuleId]: mergedClientInfoRuleIdData });
    },
    /**
     * Unset config value
     * @param {*} state - current state
     * @param {String} ClientInfoRuleId - rule id
     * @param {String} category - category of config item
     * @param {String} fieldId - identifier of config item
     */
    UNSET_CONFIG_VALUE(state, { ClientInfoRuleId, category, fieldId, parentField }) {
        const currentClientInfoRuleIdData = state.updatedValues[ClientInfoRuleId] || {};
        const currentCategoryData = state.updatedValues[ClientInfoRuleId] && state.updatedValues[ClientInfoRuleId][category] || {};
        if (parentField) {
            delete currentCategoryData[parentField][fieldId];
        } else {
            delete currentCategoryData[fieldId];
        }
        const mergedClientInfoRuleIdData = Object.assign({}, currentClientInfoRuleIdData, { [category]: currentCategoryData });
        state.updatedValues = Object.assign({}, state.updatedValues, { [ClientInfoRuleId]: mergedClientInfoRuleIdData });

    },
    /**
     * Clear updated config values for new config
     * @param {*} state - current state 
     */
    CLEAR_NEW_CONFIG_VALUES(state) {
        state.updatedValues = {};
    },
    /**
     * Add tab to list of tabs
     * @param {String} ClientInfoRuleId - rule id
     */
    OPEN_TAB(state, { ClientInfoRuleId, ClientId }) {
        const currentTabs = state.tabs;
        if (!currentTabs.find(tab => tab.ClientInfoRuleId === ClientInfoRuleId)) {
            state.tabs.push({ name: '', ClientInfoRuleId, ClientId, isLoading: true, showChangeHistory: false, subTabValid: {} });
        }
    },
    /**
     * Remove tab from list of tabs
     * @param {String} ClientInfoRuleId - rule id
     */
    CLOSE_TAB(state, { ClientInfoRuleId }) {
        const currentTabs = state.tabs;
        state.tabs = currentTabs.filter(tab => tab.ClientInfoRuleId !== ClientInfoRuleId);
    },
    /**
     * Remove all tabs
     */
    CLOSE_ALL_TABS(state) {
        state.tabs = [];
    },
    /**
     * Set active tab
     * @param {String} ClientInfoRuleId - rule id
     */
    SET_TAB(state, ClientInfoRuleId) {
        state.activeTab = ClientInfoRuleId;
    },
    /**
     * Set name displayed for tab
     * @param {String} ClientInfoRuleId - rule id
     * @param {String} name - tab name
     */
    SET_TAB_NAME(state, { ClientInfoRuleId, name }) {
        const tabToUpdate = state.tabs.findIndex(tab => tab.ClientInfoRuleId === ClientInfoRuleId);
        state.tabs[tabToUpdate]['name'] = name;
        state.tabs[tabToUpdate]['isLoading'] = false;
    },
    /**
     * Open the change history panel
     * @param {*} state - current state
     */
    OPEN_CHANGE_HISTORY(state, { ClientInfoRuleId }) {
        const tabToUpdate = state.tabs.findIndex(tab => tab.ClientInfoRuleId === ClientInfoRuleId);
        state.tabs[tabToUpdate]['showChangeHistory'] = true;
    },
    /**
     * Close the change history panel
     * @param {*} state - current state
     */
    CLOSE_CHANGE_HISTORY(state, { ClientInfoRuleId }) {
        const tabToUpdate = state.tabs.findIndex(tab => tab.ClientInfoRuleId === ClientInfoRuleId);
        if (tabToUpdate < 0) return;
        state.tabs[tabToUpdate]['showChangeHistory'] = false;
    },
    SET_SUBTAB_VALIDITY(state, { ClientInfoRuleId, tab, value }) {
        const tabToUpdate = state.tabs.findIndex(tab => tab.ClientInfoRuleId === ClientInfoRuleId);

        if (tabToUpdate < 0) return;

        const currentTabData = state.tabs[tabToUpdate] || {};
        const currentSubTabValid = state.tabs[tabToUpdate] && state.tabs[tabToUpdate]['subTabValid'] || {};

        const mergedSubTabValid = Object.assign({}, currentSubTabValid, { [tab]: value });
        const mergedTabData = Object.assign({}, currentTabData, { subTabValid: { ...currentSubTabValid, ...mergedSubTabValid } });

        state.tabs = Object.assign([], state.tabs, { [tabToUpdate]: mergedTabData });

    },
    /**
     * Set bulk update ids
     * @param {Array} ClientIds - array of ids of clients
     */
    SET_BULK_UPDATE_IDS(state, { ClientIds }) {
        state.bulkUpdateIds = ClientIds;
    },
    /**
     * Remove bulk update id
     * @param {Array} ClientId - id of client
     */
    REMOVE_BULK_UPDATE_ID(state, { ClientId }) {
        const currentIds = state.bulkUpdateIds;
        state.bulkUpdateIds = currentIds.filter(id => id !== ClientId);
    },
    /**
     * Set bulk update mode
     * @param {String} mode - bulk update mode (edit or delete)
     */
    SET_BULK_UPDATE_MODE(state, mode) {
        state.bulkUpdateMode = mode;
    },
    /**
     * Reset bulk settings
     */
    RESET_BULK_SETTINGS(state) {
        state.bulkUpdateIds = [];
        state.bulkUpdateMode = undefined;
        state.updatedValues.bulkEdit = {};
        state.updatedValues.bulkDelete = {};
    },
    /**
     * Copy an existing config to the new config
     */
    COPY_CONFIG_TO_NEW(state, { ClientInfoRuleId }) {
        let configToCopy = state.config[ClientInfoRuleId] || {};
        // don't copy over label to new config
        if (configToCopy.Info) {
            if (configToCopy.Info.ConfigLabel) {
                configToCopy.Info.ConfigLabel = undefined;
            }
            if (configToCopy.Info.ClientId) {
                configToCopy.Info.CopyFromClientId = configToCopy.Info.ClientId;
            }
        }
        // don't copy over any fields flagged as personal details (which currently only exist in ClientInformation)
        if (configToCopy.ClientInformation) {
            configToCopy.ClientInformation = Object.fromEntries(
                Object.entries(configToCopy.ClientInformation).filter(([fieldId]) => {
                    return !getFieldMapValue({ category: 'ClientInformation', fieldId, value: 'personalDetail' }) === true;
                })
            );
        }

        state.updatedValues = Object.assign({}, state.updatedValues, { New: configToCopy });
    },
    /**
     * Set Updated Values
     */
    SET_UPDATED_VALUES(state, { ClientInfoRuleId, updatedValues }) {
        state.updatedValues[ClientInfoRuleId] = updatedValues;
    },
    /**
     * Set promote clients
     * @param {Array} clients - array of ids of clients and info rule ids
     */
    SET_DISCARD_CLIENT(state, client) {
        state.discardClient = client;
    },
    /**
     * Set promote clients
     * @param {Array} clients - array of ids of clients and info rule ids
     */
    SET_PROMOTE_CLIENTS(state, { clients }) {
        state.promoteClients = clients;
    },
    /**
     * Update a promote client
     */
    UPDATE_PROMOTE_CLIENT(state, { ClientId, ChangeDetails, ChangeTitle }) {
        const clientToUpdate = state.promoteClients.findIndex(client => client.clientId === ClientId);
        state.promoteClients[clientToUpdate]['ChangeDetails'] = ChangeDetails;
        state.promoteClients[clientToUpdate]['ChangeTitle'] = ChangeTitle;
    },
    /**
     * Remove promote client
     * @param {Array} ClientId - id of client
     */
    REMOVE_PROMOTE_CLIENT(state, { ClientId }) {
        const currentClients = state.promoteClients;
        state.promoteClients = currentClients.filter(client => client.clientId !== ClientId);
    },
    /**
     * Set the promotion state
     * @param {String} clientInfoRuleId - rule id
     * @param {boolean} isPromoted 
     */
    SET_IS_PROMOTED(state, { clientInfoRuleId, isPromoted }) {
        // Update open configs
        if (state.config[clientInfoRuleId] && state.config[clientInfoRuleId].Info) {
            state.config[clientInfoRuleId].Info.IsPromoted = isPromoted;
        }
    },
    VALIDATE_CONFIG(state, configName) {
        state.validationRequests = Object.assign({}, state.validationRequests, { [configName]: true });
    },
    CLEAR_VALIDATE_CONFIG(state, configName) {
        state.validationRequests = Object.assign({}, state.validationRequests, { [configName]: false });
    }
};

/*
    Getters are non-cached access points to the state data.
    They allow us to interrogate the data and pull back dynamic subsets.
*/

export const getters = {
    /**
     * get configuration list
     * @param {*} state - current state
     * @returns Array of config
     */
    getList: state => { return state.list },
    /**
     * get config template list
     * @param {*} state - current state
     * @returns Array of config
     */
    getTemplateList: state => { return state.templateList },
    /**
     * get existing ids
     * @param {*} state - current state
     * @returns Array of ids
     */
    getExistingIds: state => { return state.existingIds },
    /**
     * get change list
     * @param {*} state - current state
     * @returns Array of changes
     */
    getChangeList: state => { return state.changeList },
    /**
     * get configuration data
     * @param {*} state - current state
     * @returns Object of config data
     */
    getConfig: state => { return state.config },
    /**
     * get configuration data
     * @param {*} state - current state
     * @returns Object of config template data
     */
    getConfigDefaults: (state, getters) => {
        const configTemplate = getters.getConfigTemplate;
        const configList = Object.keys(getters.getConfig).map(configId => { return { ClientInfoRuleId: configId } });

        let defaults = {};
        const findDefaults = ({ availableFieldList, category, parentField, ruleId }) => {
            for (const field of availableFieldList) {
                if (field.DefaultValue) {
                    const currentRuleId = defaults[ruleId] || {};
                    const currentCategory = defaults[ruleId] && defaults[ruleId][category] || {};

                    let updatedCategory;
                    if (parentField) {
                        const currentParentField = defaults[ruleId] && defaults[ruleId][category] && defaults[ruleId][category][parentField] || {};
                        const updatedParentField = Object.assign({}, currentParentField, { [field.FieldId]: field.DefaultValue });
                        updatedCategory = Object.assign({}, currentCategory, { [parentField]: updatedParentField });
                    } else {
                        updatedCategory = Object.assign({}, currentCategory, { [field.FieldId]: field.DefaultValue });
                    }

                    const updatedRuleId = Object.assign({}, currentRuleId, { [category]: updatedCategory });
                    defaults = Object.assign({}, defaults, { [ruleId]: updatedRuleId });
                }

                if (field.Options) {
                    for (const f of field.Options) {
                        if (f.DefaultValue) {
                            const currentRuleId = defaults[ruleId] || {};
                            const currentCategory = defaults[ruleId] && defaults[ruleId][category] || {}

                            let updatedCategory;
                            if (parentField) {
                                const currentParentField = defaults[ruleId] && defaults[ruleId][category] && defaults[ruleId][category][parentField] || {};
                                const updatedParentField = Object.assign({}, currentParentField, { [f.FieldId]: f.DefaultValue });
                                updatedCategory = Object.assign({}, currentCategory, { [parentField]: updatedParentField });
                            } else {
                                updatedCategory = Object.assign({}, currentCategory, { [f.FieldId]: f.DefaultValue });
                            }

                            const updatedRuleId = Object.assign({}, currentRuleId, { [category]: updatedCategory });
                            defaults = Object.assign({}, defaults, { [ruleId]: updatedRuleId });
                        }
                        if (f.AvailableFieldList) {
                            findDefaults({ availableFieldList: f.AvailableFieldList, category, parentField: f.FieldId, ruleId })
                        }
                    }
                }
                if (field.AvailableFieldList) {
                    findDefaults({ availableFieldList: field.AvailableFieldList, category, parentField: field.FieldId, ruleId })
                }
            }
        }

        const loadedRuleIds = [...configList.map(client => client.ClientInfoRuleId), ...specialRuleIds];
        for (const ruleId of loadedRuleIds) {
            for (const [category, templateFields] of Object.entries(configTemplate)) {
                if (templateFields.AvailableFieldList) {
                    findDefaults({ availableFieldList: templateFields.AvailableFieldList, category, ruleId });
                }
            }
        }
        return defaults;
    },
    getConfigTemplate: state => { return state.template },
    getConfigVisibility: (state, getters) => {
        const { config, updatedValues } = state;

        // using lodash as this needs to be a deep merge
        const mergedConfig = configMerge({}, getters.getConfigDefaults, config, updatedValues);
        // This contains any currently open templates or clients
        const configList = Object.keys(getters.getConfig).map(configId => { return { ClientInfoRuleId: configId } });

        return visibleFields({ config: mergedConfig, configList, configTemplate: getters.getConfigTemplate, roles: getters.getRoles });
    },
    getTemplateConfigs: (state, getters) => {
        return existsInTemplate({ configTemplate: getters.getConfigTemplate });
    },
    /**
     * get updated configuration values
     * @param {*} state - current state
     * @returns Object of config values that have been updated
     */
    getUpdatedConfigValues: (state, getters) => {
        const { updatedValues } = state;
        const { getConfigVisibility, } = getters;


        const filterValuesBasedOnVisibility = (values, visibility) => {
            if (!values || typeof values !== 'object' || !visibility || typeof visibility !== 'object') {
                return values;
            }

            const result = {};
            Object.keys(values).forEach(key => {
                // Bulk delete doesn't have visiblity concerns, it should show all values
                if (key === 'bulkDelete' || key === 'Info') {
                    result[key] = values[key];
                    return;
                }

                if (Object.prototype.hasOwnProperty.call(visibility, key)) {
                    if (typeof values[key] === 'object' && typeof visibility[key] === 'object') {
                        const subResult = filterValuesBasedOnVisibility(values[key], visibility[key]);
                        if (Object.keys(subResult).length > 0) {
                            result[key] = subResult;
                        }
                    } else if (visibility[key] === true) {
                        result[key] = values[key];
                    }
                }
            });
            return result;
        };

        return filterValuesBasedOnVisibility(updatedValues, getConfigVisibility);
    },
    /**
     * values returned from API along with any values that have been updated by the user
     * @param {*} state - current state
     * @returns Object of config values that have been updated
     */
    getMergedConfig: (state, getters) => {
        const { config } = state;
        const { getConfigDefaults, getUpdatedConfigValues } = getters;

        return configMerge({}, getConfigDefaults, config, getUpdatedConfigValues);
    },
    /**
     * are there updated config values
     * @param {*} state - current state
     * @returns Object of booleans if config values exists for a given rule id
     */
    hasUpdatedConfigValues: state => {
        const loadedRuleIds = [...state.list.map(client => client.ClientInfoRuleId), ...state.templateList.map(client => client.ClientInfoRuleId), ...specialRuleIds];
        return Object.fromEntries(loadedRuleIds.map(id =>
            ([id, !!(state.updatedValues[id] && Object.keys(state.updatedValues[id]).length)])
        ));
    },
    /**
     * get tabs
     * @param {*} state - current state
     * @returns Array of open tabs
     */
    getTabs: state => { return state.tabs },
    /**
     * get currently active tab
     * @param {*} state - current state
     * @returns Object of active tab data
     */
    getActiveTab: state => {
        const activeTab = state.activeTab;
        const allTabs = state.tabs;

        const tab = allTabs.find(tab => tab.ClientInfoRuleId === activeTab);

        return tab || { ClientInfoRuleId: 'clientManagement', showChangeHistory: false, subTabValid: {} };
    },
    /**
     * get bulk update ids
     * @param {*} state - current state
     * @returns Array of clients being updated
     */
    getBulkUpdateIds: state => { return state.bulkUpdateIds },
    /**
     * get count of bulk update items
     * @param {*} state - current state
     * @returns Number of items for bulk update
     */
    getBulkUpdateCount: state => { return state.bulkUpdateIds.length },
    /**
     * get bulk update mode
     * @param {*} state - current state
     * @returns string of bulk update mode (edit or delete)
     */
    getBulkUpdateMode: state => { return state.bulkUpdateMode },
    /**
     * get promote clients
     * @param {*} state - current state
     * @returns Array of clients being promoted
     */
    getDiscardClient: state => { return state.discardClient },
    getPromoteClients: state => { return state.promoteClients },
    getValidationRequests: state => { return state.validationRequests },
}
/**
 * expand the model of any embedded properties with "." dot notation
 * @param {Object} configDataObject - config object
 * @returns {Object} expanded object
 */
function expandConfigData(configDataObject) {
    for (var propt in configDataObject) {
        if (typeof configDataObject[propt] === 'object') {
            configDataObject[propt] = expandConfigData(configDataObject[propt]);
        } else {
            if (propt.includes(".")) {
                const newProps = propt.split(".");
                const newValue = configDataObject[propt];
                if (configDataObject[newProps[0]] === undefined) {
                    configDataObject[newProps[0]] = {};
                }
                configDataObject[newProps[0]][newProps[1]] = newValue;
                delete configDataObject[propt];
            }
        }
    }
    return configDataObject;
}

/**
 * Recursively iterates over available field list and fill in option list if its referencing reusable assets
 * 
 * @param {Array} assets array of asset objects
 * @param {Array} availableFieldList array of field objects to search for replacements
 * @return {Array} array of field objects with correct options
*/
function replaceAssetReferences(assets, availableFieldList) {
    let fieldList = [];
    for (let field of availableFieldList) {
        if (field.Source !== undefined) {
            const asset = assets[field.Source];
            const displayRef = field.OptionMapping.DisplayRef;
            const valueRef = field.OptionMapping.ValueRef;
            let options = [];
            for (const option of asset.Items) {
                const newOption = { ["Display"]: option[displayRef], ["Value"]: option[valueRef] };
                if (options.filter(el => el.Value === newOption.Value).length === 0) {
                    //if the value is not already in the list, lets add it
                    newOption.VisibilityConditions = option.VisibilityConditions;
                    newOption.VisibilityType = option.VisibilityType;
                    options.push(newOption);
                }
            }
            field.Options = options;
        }
        if (field.AvailableFieldList) {
            field.AvailableFieldList = replaceAssetReferences(assets, field.AvailableFieldList);
        }
        if (field.Options){
            for(const option of field.Options){
                if(option.AvailableFieldList){
                    option.AvailableFieldList = replaceAssetReferences(assets, option.AvailableFieldList);
                }
            }
        }
        fieldList.push(field);
    }
    return fieldList;
}

/**
 * Recursively iterates over template fields and replaces them with config lists if necessary
 * 
 * @param {Object} template current template
 * @param {Object} config current config
 * @return {Object} updated template
*/
function replaceConfigReferences(ruleId, template, config) {
    const innerReplaceConfigReferences = (availableFieldList) => {
        let fieldList = [];
        for (let field of availableFieldList) {
            if (field.ConfigSource && field.ConfigSource.includes(".")) {
                const fieldRefProps = field.ConfigSource.split(".");
                let refConfigValue =
                    config[ruleId] &&
                    config[ruleId][fieldRefProps[0]] &&
                    config[ruleId][fieldRefProps[0]][fieldRefProps[1]];
                if (fieldRefProps.length === 3) {
                    refConfigValue = refConfigValue && config[ruleId][fieldRefProps[0]][fieldRefProps[1]][fieldRefProps[2]];
                }
                const displayRef = field.ConfigOptionMapping.DisplayRef;
                const valueRef = field.ConfigOptionMapping.ValueRef;
                let options = [];
                if (refConfigValue && Array.isArray(refConfigValue)) {
                    for (const option of refConfigValue) {
                        const newOption = { ["Display"]: option[displayRef], ["Value"]: option[valueRef] };
                        if (options.filter(el => el.Value === newOption.Value).length === 0) {
                            //if the value is not already in the list, lets add it
                            options.push(newOption);
                        }
                    }
                }
                field.Options = options;
            }
            if (field.AvailableFieldList) {
                field.AvailableFieldList = innerReplaceConfigReferences(field.AvailableFieldList);
            }
            fieldList.push(field);
        }
        return fieldList;
    }
    for (let property in template) {
        if (property !== "TemplateAssets") {
            template[property]["AvailableFieldList"] = innerReplaceConfigReferences(template[property]["AvailableFieldList"]);
        }
    }
    return template;
}

/**
 * Filter Config rule based on what template supports
 * 
 * @param {Object} clientInfoRule client info to transform
 * @param {Object} template template to base transformation on
 * @return {Object} JSON object of decoded authorization token
*/
function transformConfigFromTemplate(clientInfoRule, template) {

    const doesTemplateHaveFieldId = function (currentTemplateObject, fieldId) {
        let containsField = false;
        if (currentTemplateObject.FieldId === fieldId) {
            containsField = true;
        } else if (currentTemplateObject.AvailableFieldList) {
            if (currentTemplateObject.AvailableFieldList.some(field => field.FieldId === fieldId)) {
                containsField = true;
            } else {
                for (const subField of currentTemplateObject.AvailableFieldList) {
                    containsField = doesTemplateHaveFieldId(subField, fieldId);
                    if (containsField) {
                        break;
                    }
                }
            }
        } else if (currentTemplateObject.Options && currentTemplateObject.Options.some(option => option.AvailableFieldList !== undefined)) {
            var optionWithFields = currentTemplateObject.Options.filter(option => option.AvailableFieldList !== undefined);
            for (const option of optionWithFields) {
                containsField = doesTemplateHaveFieldId(option, fieldId);
                if (containsField) {
                    break;
                }
            }
        }
        return containsField;
    }

    const evaluateTemplateFields = function (currentConfigObject, currentTemplateObject) {
        for (const configFieldId of Object.keys(currentConfigObject)) {
            if (doesTemplateHaveFieldId(currentTemplateObject, configFieldId)) {
                if (Array.isArray(currentConfigObject[configFieldId])) {
                    for (const currItem in currentConfigObject[configFieldId]) {
                        if (typeof currItem === 'object') {
                            evaluateTemplateFields(currItem, currentTemplateObject);
                        }
                    }
                } else if (typeof currentConfigObject[configFieldId] === 'object') {
                    evaluateTemplateFields(currentConfigObject[configFieldId], currentTemplateObject);
                }
            } else if (configFieldId !== "IsEnabled") {
                //if our template doesn't have the field, lets delete it
                delete currentConfigObject[configFieldId];
            }
        }
    }

    for (const category of Object.keys(clientInfoRule)) {
        if (template[category] === undefined) {
            //if our template doesn't have category, lets delete it
            delete clientInfoRule[category];
        } else {
            evaluateTemplateFields(clientInfoRule[category], template[category]);
        }
    }
    return clientInfoRule;
}